import { Input, Space, Table } from 'antd';
import { useRouter } from 'next/router';
import { ChangeEventHandler, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import GrispiTypographyNextLink from '../../../../components/GrispiTypographyNextLink';
import { MAX_CHAR_LENGTH } from '../../../../constants/input';
import useDebounce from '../../../../hooks/useDebounce';
import useTicketSearch from '../../../../hooks/useTicketSearch';
import { Ticket } from '../../../../model/types';
import { getUserCredentialsToShow } from '../../../../utils/helpers';

const { Search } = Input;

export default function SearchDrawerContainer({ closeDrawer, open }: { closeDrawer: () => void; open: boolean }) {
    const { push } = useRouter();
    const { t } = useTranslation('tables');

    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearch = useDebounce(searchTerm, 500);
    const { data, isLoading } = useTicketSearch(debouncedSearch, open);
    const ticketList = data?.content;

    const searchEl = useRef(null);
    useEffect(() => {
        if (searchEl.current && open) {
            searchEl.current.focus();
        }
    }, [searchEl, open, searchTerm]);

    const columns = [
        {
            title: t('column.ticketNo.label'),
            dataIndex: 'key',
            key: 'key',
            width: 100
        },
        {
            title: t('column.subject.label'),
            dataIndex: 'subject',
            key: 'subject',
            render: (value: string, record) => (
                <GrispiTypographyNextLink href={`/tickets/${record.key}`}>{value}</GrispiTypographyNextLink>
            ),
            ellipsis: true
        },
        {
            title: t('column.requester.label'),
            dataIndex: 'requester',
            key: 'requester',
            render: (value: Ticket['requester']) => getUserCredentialsToShow(value)
        }
    ];

    const handleOnChange: ChangeEventHandler<HTMLInputElement> = ({ target: { value } }) => {
        setSearchTerm(value);
    };

    const handleOnSearch: (
        value: string,
        event?:
            | React.ChangeEvent<HTMLInputElement>
            | React.MouseEvent<HTMLElement>
            | React.KeyboardEvent<HTMLInputElement>
    ) => void = (value) => {
        setSearchTerm(value);
    };

    return (
        <Space direction="vertical" size="large">
            <Search
                ref={searchEl}
                placeholder={t('placeholder.searchTicket')}
                loading={isLoading}
                allowClear
                onChange={handleOnChange}
                onSearch={handleOnSearch}
                //Value was controlled by Antd. In order to reset everytime a record clicked we need to control value.
                value={searchTerm}
                enterButton
                maxLength={MAX_CHAR_LENGTH}
            />
            <Table
                rowKey="key"
                columns={columns}
                dataSource={ticketList}
                loading={isLoading}
                onRow={(record) => {
                    return {
                        onClick: (e) => {
                            if (e.ctrlKey) return; //ticket name as a link inside the row and when user click with ctrl key Link and row's onClick triggering same time
                            push(`/tickets/${record.key}`);
                            setSearchTerm('');
                            closeDrawer();
                        } // click row
                    };
                }}
                style={{ cursor: 'pointer' }}
                size="small"
                pagination={{ position: ['bottomCenter'] }}
            />
        </Space>
    );
}
